import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { NumberFormatter } from '@repo/toolkit/number'
import Badge from '@repo/ui/badge'
import Checkbox from '@repo/ui/checkbox'
import InnerBox from '@repo/ui/inner-box'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'

import {
  getAffectedStaffPayscale,
  getAffectedStaffPayscaleQueryKey
} from '../../../api/affected-staffs/get-payscale'
import { CurrencyEnum, currencyEnumMapping } from '../../../lib/types/enums/common/currency.enum'
import { AffectedStaffInput } from '../../../lib/types/models/affectedStaff/affected-staff-input'
import { getAffectedStaffRemuneration } from '../../../lib/utils/functions/affected-staff-remuneration.functions'
import { FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledInput from '../../elements/inputs/controlled-input'
import CurrencyInput from '../../elements/inputs/currency-input'

type Props = {
  title?: string
  staffId?: number
  staffRequestId?: number
  readonly?: boolean
  additionalComponent?: React.ReactNode
}

const AffectedStaffPayscale = ({
  title,
  staffId,
  staffRequestId,
  readonly,
  additionalComponent
}: Props) => {
  const form = useFormContext<AffectedStaffInput>()
  const [modifyPrice, setModifyPrice] = useState(!!form.getValues('baseSalary'))

  const { data: payScale } = useQuery({
    queryKey: getAffectedStaffPayscaleQueryKey(staffId, staffRequestId),
    queryFn: () => {
      if (!!staffId && !!staffRequestId) return getAffectedStaffPayscale(staffId, staffRequestId)
    },
    enabled: !!staffId && !!staffRequestId
  })

  const { exceptionalBonusAmount, baseSalary, currency } = useWatch({ control: form.control })

  // The pay scale currency is derived from the qualification by the backend, so it is the
  // reference. The form currency only takes over when the remuneration is manually overridden.
  const displayedCurrency =
    (baseSalary != null ? currency : undefined) ?? payScale?.currency ?? currency

  const { formattedSalary, formattedTotal, bonusText } = useMemo(() => {
    const { salary, total } = getAffectedStaffRemuneration(
      { baseSalary, exceptionalBonusAmount },
      payScale
    )

    const formattedSalary = NumberFormatter.formatCurrency(salary, displayedCurrency)
    const formattedTotal = NumberFormatter.formatCurrency(total, displayedCurrency)
    const bonusText = payScale?.bonusPrice
      ? `(dont ${NumberFormatter.formatCurrency(payScale?.bonusPrice ?? 0, displayedCurrency)} ${payScale?.name ?? ''})`
      : ''

    return { formattedSalary, formattedTotal, bonusText }
  }, [baseSalary, exceptionalBonusAmount, payScale, displayedCurrency])

  return (
    <InnerBox className="flex flex-col gap-4 rounded-lg p-3" title={title} hasTitleSpace={false}>
      <div className="flex justify-between items-center ">
        <Badge
          text={`Rémunération : ${formattedSalary} ${!baseSalary ? bonusText : ''}`}
          color={tailwindColors.white.active}
          textColor={tailwindColors.primary.DEFAULT}
        />
        <Badge variant="warning" text={`Total : ${formattedTotal}`} />
      </div>
      <div className="flex gap-3">
        <Checkbox
          disabled={readonly}
          label={'Modifier la rémunération'}
          checked={modifyPrice}
          onChange={(checked) => {
            setModifyPrice(checked)
            if (!checked) {
              form.setValue('baseSalary', null)
              form.setValue('currency', payScale?.currency ?? currency ?? CurrencyEnum.EUR)
            }
          }}
        />
        {modifyPrice && (
          <CurrencyInput
            readonly={readonly}
            value={{
              selectedOptionValue: form.watch('currency'),
              value: form.watch('baseSalary')?.toString()
            }}
            onChange={(value) => {
              if (value.selectedOptionValue) {
                const amount = value.value ? +value.value : undefined
                form.setValue('baseSalary', amount, { shouldValidate: true })
                form.setValue('currency', value.selectedOptionValue, {
                  shouldValidate: true
                })
                // Check if the value becomes 0, if so, remove the budget handled
                if (!amount || amount === 0) {
                  form.resetField('baseSalary')
                  form.resetField('currency')
                }
              }
            }}
          />
        )}
      </div>
      <div className="flex flex-col gap-2">
        <Typography variant="tag" color={tailwindColors.grey.DEFAULT}>
          Prime exceptionnelle
        </Typography>
        <div className="flex gap-3">
          <FormField
            control={form.control}
            name="exceptionalBonusReason"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledInput {...field} readOnly={readonly} type="text" label="Label" />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="exceptionalBonusAmount"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledInput
                    {...field}
                    readOnly={readonly}
                    type="number"
                    label={`Montant (${currencyEnumMapping[displayedCurrency ?? CurrencyEnum.EUR].symbol})`}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
      </div>
      {additionalComponent}
    </InnerBox>
  )
}

export default AffectedStaffPayscale
